import { memo, type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { type ContentType } from 'TutorBotConversation';

type Props = {
    children: ReactNode;
    isHumanMessage: boolean;
    isLoading?: boolean;
    contentType: ContentType;
    className?: string;
    isOneSidedConversation?: boolean;
};

const coloredBackground = [
    'bg-gradient-to-r',
    'text-white',
    'from-coral to-orange-500-alt',
    'valar:from-blue-500-alt valar:to-blue-400-alt',
    'turquoise-frame:from-turquoise-600-alt turquoise-frame:to-turquoise-500-alt',
    'purple-frame:from-purple-500-alt purple-frame:to-purple-450-alt',
    'blue-frame:from-blue-500-alt blue-frame:to-blue-400-alt',
];
const whiteBackground = (contentType: string) =>
    `border border-slate-grey-200 bg-white shadow-smallish ${
        contentType === 'text' ? 'text-black' : 'text-tutorbot-chat'
    }`;
const greyBackground = 'bg-slate-grey-200 text-slate-grey';

const alignedRight = 'ml-auto rtl:ml-0 rtl:mr-auto';

const getMessageContentClasses = (
    isHumanMessage: boolean,
    contentType: ContentType,
    isLoading: boolean,
    isOneSidedConversation: boolean,
    className?: string,
) =>
    twMerge(
        'select-text flex flex-col leading-5 sm:leading-6 w-fit',

        // in a conversation that only has messages from one person (like the greeting messages in VoiceAgentRooom intro),
        // allow the message to stretch the full length
        isOneSidedConversation ? '' : 'max-w-[75%]',
        isHumanMessage && alignedRight,
        isLoading && 'py-[13px]',

        // background, border, text color
        isHumanMessage && contentType === 'text' && coloredBackground,
        isHumanMessage && contentType === 'transcribed_audio' && greyBackground,
        !isHumanMessage && whiteBackground(contentType),

        // border radius
        contentType === 'text' && 'rounded-6',
        contentType === 'transcribed_audio' && 'rounded-11',

        // margin
        contentType === 'text' && 'mb-2.5 sm:mb-3',
        contentType === 'transcribed_audio' && 'mb-5',

        // padding
        contentType === 'text' && 'p-3.5',
        contentType === 'transcribed_audio' && 'p-3',

        // italics
        contentType === 'transcribed_audio' && isHumanMessage && 'italic',
        className,
    );

function TutorBotMessageContentComponent({
    children,
    isHumanMessage,
    contentType,
    isOneSidedConversation = false,
    isLoading = false,
    className,
}: Props) {
    return (
        <div
            data-testid="message-content"
            className={getMessageContentClasses(
                isHumanMessage,
                contentType,
                isLoading,
                isOneSidedConversation,
                className,
            )}
        >
            {children}
        </div>
    );
}

export const TutorBotMessageContent = memo(TutorBotMessageContentComponent) as typeof TutorBotMessageContentComponent;
